import { Box, Icon, Image, Theme, Typography } from "@outschool/backpack";
import {
  faArrowRight,
  faBackpack,
  faCompass,
  faDiploma
} from "@outschool/icons";
import { TFunction, useTranslation } from "@outschool/localization";
import { onboardingQuizPath } from "@outschool/routes";
import { TrackedButton } from "@outschool/ui-components-shared";
import {
  ParentStories,
  TeacherStories,
  TrustpilotMicroCombo
} from "@outschool/ui-components-website";
import { Carousel, Container } from "@outschool/ui-legacy-component-library";
import { useIsMobile, useNavigation } from "@outschool/ui-utils";
import React from "react";

import { DesktopHeroImage } from "../HeroComponents";

export const PaidCategoryHeroSection = ({
  title,
  heroImageUrl,
  tryTriggerSignupModal
}: {
  title: string;
  heroImageUrl: string;
  tryTriggerSignupModal: () => boolean;
}) => {
  const { t } = useTranslation(
    "client\\components\\categories\\paid\\PaidCategoryHeroSection"
  );
  const heroDescription = t(
    "Compare expert educators, book live sessions, and watch your child excel."
  );
  const navigate = useNavigation();
  const ctaOnClick = () =>
    tryTriggerSignupModal() || navigate(onboardingQuizPath());
  const isMobile = useIsMobile();
  return (
    <>
      <Box sx={{ backgroundColor: "#f0f3ff" }}>
        <Container>
          <Box
            flex
            sx={(theme: Theme) => ({
              [theme.breakpoints.up("md")]: {
                paddingTop: "5em"
              },
              [theme.breakpoints.down("md")]: {
                flexDirection: "column"
              }
            })}
          >
            <Box
              flex
              sx={{
                flexDirection: "column",
                gap: "0.5em",
                paddingRight: isMobile ? undefined : "5em"
              }}
            >
              {!isMobile && <TrustpilotMicroCombo />}
              <Typography
                variant="display2"
                sx={(theme: Theme) => ({
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                  marginBottom: theme.spacing(2),
                  textAlign: isMobile ? "center" : undefined
                })}
              >
                {title}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={(theme: Theme) => ({
                  marginBottom: "1em",
                  [theme.breakpoints.down("md")]: {
                    textAlign: "center"
                  }
                })}
              >
                {heroDescription}
              </Typography>

              <Box
                sx={(theme: Theme) => ({
                  [theme.breakpoints.down("md")]: {
                    display: "flex",
                    justifyContent: "center"
                  }
                })}
              >
                <TrackedButton variant="contained" onClick={ctaOnClick}>
                  <Box
                    flex
                    sx={{ justifyContent: "space-between", gap: "1em" }}
                  >
                    {t("Find the perfect class")}
                    <Icon icon={faArrowRight} />
                  </Box>
                </TrackedButton>
              </Box>
            </Box>
            <Box
              flex
              sx={(theme: Theme) => ({
                justifyContent: "center",
                alignItems: "center",
                [theme.breakpoints.down("md")]: {
                  marginTop: "5em",
                  marginBottom: "1em"
                }
              })}
            >
              <Box
                flex
                sx={(theme: Theme) => ({
                  width: "100%",
                  height: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                  [theme.breakpoints.down("md")]: {
                    width: "50%",
                    height: "50%"
                  }
                })}
              >
                <DesktopHeroImage imageUrl={heroImageUrl} withSkeleton />
              </Box>
            </Box>
          </Box>
          {<PressLogos />}
        </Container>
      </Box>
      {!isMobile && <OutschoolStats t={t} />}
    </>
  );
};

const OutschoolStats = ({ t }: { t: TFunction }) => {
  const stats = [
    {
      title: t("64,000+ classes"),
      caption: t(
        "Explore every topic—from coding to cooking—live and interactive."
      ),
      icon: faBackpack
    },
    {
      title: t("4,800+ vetted teachers"),
      caption: t(
        "All background-checked pros, dedicated to your child’s growth."
      ),
      icon: faCompass
    },
    {
      title: t("1+ million kids"),
      caption: t(
        "Parents around the globe trust us for flexible, worry-free learning."
      ),
      icon: faDiploma
    }
  ];

  return (
    <Box
      flex
      sx={{
        flexWrap: "wrap",
        gap: "1em",
        justifyContent: "center",
        padding: "2em"
      }}
    >
      {stats.map((stat, index) => {
        return (
          <>
            <Box
              key={index}
              flex
              sx={{
                flexDirection: "column",
                gap: "0.5em",
                alignItems: "center",
                justifyContent: "center",
                maxWidth: "300px"
              }}
            >
              <Icon icon={stat.icon} size="medium" />
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 400,
                  textAlign: "center"
                }}
              >
                {stat.title}
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  textAlign: "center"
                }}
              >
                {stat.caption}
              </Typography>
            </Box>
          </>
        );
      })}
    </Box>
  );
};

const PressLogos = () => {
  const icons = [
    "Forbes Magazine",
    "Wall Street Journal",
    "NPR",
    "New York Times",
    "FastCompany",
    "CBS"
  ]
    .map(
      storyName =>
        [...ParentStories, ...TeacherStories].find(
          ({ name }) => name === storyName
        )?.img
    )
    .filter(Boolean);
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Carousel>
        <Box
          flex
          sx={{
            gap: "1em",
            marginTop: "1em",
            marginBottom: "1em",
            justifyContent: "center"
          }}
        >
          {icons.map((icon, index) => (
            <Image key={index} src={icon} sx={{ height: "2.5em" }} />
          ))}
        </Box>
      </Carousel>
    );
  }
  return (
    <Box
      flex
      sx={{
        paddingTop: "1em",
        paddingBottom: "2em",
        alignItems: "center",
        flexDirection: "column"
      }}
    >
      <Box
        flex
        sx={{
          gap: "1em",
          flexWrap: "wrap",
          marginTop: "1em",
          justifyContent: "center"
        }}
      >
        {icons.map((icon, index) => (
          <Image key={index} src={icon} sx={{ height: "2.5em" }} />
        ))}
      </Box>
    </Box>
  );
};
